import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'

/** COMPONENT */
import AutocompleteSelect from './autoCompleteSelect'

/** CONSTANTS */
import { LIMIT_ALL, LIMIT_SEARCH } from '../../constants/pagination'

/** UTILS */
import { filterActiveOptions } from '../../utils/app.utils'

type SearchAutocompleteSelectProps = {
    labelId: string
    heading?: string
    headingNormal?: boolean
    placeholder?: string
    /** id ที่เลือก (0 / null = ทั้งหมด) */
    value: number | null | undefined
    /** ชื่อ field id ของ option เช่น 'idDepartment' */
    idKey: string
    getOptionLabel: (option: any) => string
    getOptionDisabled?: (option: any) => boolean
    /** ชื่อ field สถานะ เช่น 'departmentStatus' ถ้าระบุจะแสดงเฉพาะรายการที่เปิดใช้งาน (คงรายการที่เลือกไว้แล้ว) */
    statusKey?: string
    /** เรียก list API เช่น (condition) => DepartMentApi.getDepartments(condition) */
    fetchOptions: (condition: any) => Promise<any>
    /** param กรองเพิ่มเติม เช่น { idFiscalYear } (ค่า 0 / null จะไม่ถูกส่ง) */
    params?: Record<string, any>
    /** true = ค้นหาผ่าน API (keyword) / false = ดึงทั้งหมดครั้งเดียวแล้วกรองฝั่ง client */
    serverSearch?: boolean
    onChange: (id: number, option: any | null) => void
    disabled?: boolean
    disableClearable?: boolean
}

export default function SearchAutocompleteSelect(props: SearchAutocompleteSelectProps) {
    const { t } = useTranslation()
    const { idKey, value, params, serverSearch = true } = props

    const [options, setOptions] = useState<any[]>([])
    const [selected, setSelected] = useState<any>(null)
    const [keyword, setKeyword] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)

    const fetchRef = useRef(props.fetchOptions)
    fetchRef.current = props.fetchOptions

    // debounce แยกต่อช่อง ไม่ใช้ debouncedApiCall กลาง
    const debouncedSetKeyword = useMemo(() => _.debounce((val: string) => setKeyword(val), 400), [])
    useEffect(() => () => debouncedSetKeyword.cancel(), [debouncedSetKeyword])

    const paramsKey = JSON.stringify(params || {})
    useEffect(() => {
        let active = true
        const extraParams = _.pickBy(JSON.parse(paramsKey), (val) => val !== undefined && val !== null && val !== 0 && val !== '')
        let condition: any = { page: 1, limit: serverSearch ? LIMIT_SEARCH : LIMIT_ALL, ...extraParams }
        if (serverSearch && keyword) condition = { ...condition, keyword: keyword }
        setLoading(true)
        fetchRef.current(condition)
            .then((res: any) => {
                if (active && res?.status === 200) setOptions(res.data?.result?.items || [])
            })
            .catch((error: any) => {
                console.log(error)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [keyword, paramsKey, serverSearch])

    // sync ค่าที่เลือกกับ value (เช่น ถูก reset เป็น 0 จาก filter อื่น)
    useEffect(() => {
        setSelected((prev: any) => {
            if (!value) return null
            if (prev?.[idKey] === value) return prev
            return options.find((option: any) => option?.[idKey] === value) || null
        })
    }, [value, options, idKey])

    const visibleOptions = props.statusKey ? filterActiveOptions(options, props.statusKey, idKey, [value]) : options

    // เก็บ option ที่เลือกไว้ใน list เสมอ label จะได้ไม่หายเมื่อผลค้นหาเปลี่ยน
    const mergedOptions = selected && !visibleOptions.some((option: any) => option?.[idKey] === selected?.[idKey])
        ? [selected, ...visibleOptions]
        : visibleOptions

    return (
        <AutocompleteSelect
            isFilter
            labelId={props.labelId}
            heading={props.heading}
            headingNormal={props.headingNormal}
            placeholder={props.placeholder ?? 'ทั้งหมด'}
            options={mergedOptions}
            value={selected}
            getOptionLabel={(option: any) => props.getOptionLabel(option) || ''}
            getOptionDisabled={props.getOptionDisabled}
            isOptionEqualToValue={(option: any, val: any) => option?.[idKey] === val?.[idKey]}
            disableFilter={serverSearch}
            loading={loading}
            noOptionsText={t('STATUS.NOT_FOUND')}
            disabled={props.disabled}
            disableClearable={props.disableClearable}
            onInputChange={(_event: any, val: string, reason?: string) => {
                if (!serverSearch) return
                if (reason === 'input') {
                    debouncedSetKeyword(val)
                } else {
                    debouncedSetKeyword.cancel()
                    setKeyword('')
                }
            }}
            onChange={(_event: any, option: any) => {
                setSelected(option || null)
                props.onChange(option?.[idKey] || 0, option || null)
            }}
        />
    )
}
